import { INVESTIGATION_FINDING_ID_PATTERN } from "@/constants/investigationFindings"
import { z } from "zod"

export const FINDINGS_API_PATH = "/api/sofia/investigations/findings"

const FINDING_CATEGORY_LABELS: Record<string, string> = {
  config_error: "Configuration error",
  failure_pattern: "Failure pattern",
  metric_disagreement: "Metric disagreement",
  metric_improvement: "Metric improvement",
  policy_safety: "Policy & safety",
  test_case_improvement: "Test case improvement",
  unexpected_behavior: "Unexpected behavior",
}

const findingStatusSchema = z.enum([
  "open",
  "acknowledged",
  "dismissed",
  "resolved",
])

// Unknown keys are stripped rather than rejected so additive server fields never
// blank the Home section.
const findingSchema = z.object({
  briefing_priority_reason: z.string().optional(),
  category: z.string().min(1),
  evidence_count: z.number().int().min(0),
  // Same alphabet the proxy enforces on the outbound path segment: an id this parser
  // accepts is always an id the proxy can route.
  finding_id: z.string().regex(INVESTIGATION_FINDING_ID_PATTERN),
  first_seen: z.string(),
  last_seen: z.string(),
  narrative: z.string(),
  occurrence_count: z.number().int().min(0),
  reopened_from: z.string().nullable().optional(),
  pattern_id: z.string().nullable().optional(),
  previous_episode_id: z.string().nullable().optional(),
  resolution_note: z.string().nullable().optional(),
  resolved_at: z.string().nullable().optional(),
  status: findingStatusSchema,
  title: z.string(),
  version: z.number().int().min(1),
})

const freshnessEntrySchema = z
  .object({
    analyzed_through: z.string().nullable(),
    updated_at: z.string().nullable(),
  })
  .nullable()

const findingsResponseSchema = z.object({
  briefing: z
    .object({
      eligible_finding_count: z.number().int().min(0),
      generated_at: z.string(),
      selection_mode: z.enum(["window", "recent"]).optional(),
      truncated: z.boolean(),
      window_start: z.string(),
    })
    .optional(),
  findings: z.array(findingSchema),
  freshness: z.object({
    conversation: freshnessEntrySchema,
    simulation: freshnessEntrySchema,
  }),
  next_page_token: z.string().nullable(),
  workspace: z.object({
    is_default: z.boolean(),
    workspace_id: z.string(),
  }),
})

const evidenceItemSchema = z.object({
  created_at: z.string(),
  evidence_id: z.string().min(1),
  metric_id: z.string().nullable().optional(),
  metric_verdict: z.string().nullable().optional(),
  quote: z.string(),
  source_id: z.string().min(1),
  source_type: z.enum(["conversation", "simulation"]),
  speaker: z.string(),
  turn_index: z.number().int().min(0),
})

const evidenceResponseSchema = z.object({
  evidence: z.array(evidenceItemSchema),
  finding_id: z.string().regex(INVESTIGATION_FINDING_ID_PATTERN),
  next_page_token: z.string().nullable(),
})

const actionResponseSchema = z.object({
  feedback_id: z.string().optional(),
  finding: findingSchema,
})

const activityItemSchema = z.object({
  activity_id: z.string().min(1),
  actor_type: z.enum(["user", "system"]),
  created_at: z.string(),
  event_type: z.string().min(1),
  finding_version: z.number().int().min(1),
  note: z.string(),
  remediation_label: z.string(),
  remediation_url: z.string(),
})

const activityResponseSchema = z.object({
  activity: z.array(activityItemSchema),
  finding_id: z.string().regex(INVESTIGATION_FINDING_ID_PATTERN),
  next_page_token: z.string().nullable(),
})

export type InvestigationFindingStatus = z.infer<typeof findingStatusSchema>

export interface InvestigationFinding {
  briefingPriorityReason?: string
  category: string
  categoryLabel: string
  evidenceCount: number
  findingId: string
  firstSeen: string
  isRegression: boolean
  lastSeen: string
  narrative: string
  occurrenceCount: number
  patternId?: string
  previousEpisodeId?: string | null
  resolutionNote?: string | null
  resolvedAt?: string | null
  status: InvestigationFindingStatus
  title: string
  version: number
}

export interface InvestigationFreshnessEntry {
  analyzedThrough: string | null
  updatedAt: string | null
}

export interface InvestigationFindingsSnapshot {
  briefing?: {
    eligibleFindingCount: number
    generatedAt: string
    selectionMode: "window" | "recent"
    truncated: boolean
    windowStart: string
  }
  findings: InvestigationFinding[]
  // Per-source freshness only when the server proved it; null means unknown, and the
  // UI must say so instead of implying an empty backlog.
  freshness: {
    conversation: InvestigationFreshnessEntry | null
    simulation: InvestigationFreshnessEntry | null
  }
  // True when the bounded follow stopped at its request cap instead of the server's
  // null token: unvisited pages may still hold findings, and the UI must state the
  // shortfall instead of presenting the list as complete.
  truncated: boolean
  workspaceId: string
}

// One page of the findings listing. A null token is the server's proof that it
// exhausted every partition, so only then is an empty accumulation an empty backlog.
export interface InvestigationFindingsPage extends Omit<
  InvestigationFindingsSnapshot,
  "truncated"
> {
  nextPageToken: string | null
}

export interface InvestigationEvidenceItem {
  // When an investigation recorded this quote: the only time evidence carries.
  createdAt: string
  evidenceId: string
  href: string | undefined
  metricVerdict: string | null
  quote: string
  sourceId: string
  sourceLabel: string
  sourceType: "conversation" | "simulation"
  speaker: string
  turnIndex: number
}

export interface InvestigationEvidenceLoad {
  items: InvestigationEvidenceItem[]
  truncated: boolean
}

export interface InvestigationFindingActivity {
  activityId: string
  actorType: "user" | "system"
  createdAt: string
  eventType: string
  findingVersion: number
  note: string
  remediationLabel: string
  remediationUrl: string
}

interface InvestigationEvidencePage {
  items: InvestigationEvidenceItem[]
  nextPageToken: string | null
}

export type InvestigationFindingAction =
  | "reopen"
  | "acknowledge"
  | "dismiss"
  | "flag_wrong"
  | "resolve"
  | "link_remediation"

// Severity order, shared with Sofia's briefing ranking (sofia-agent
// `product/investigations/briefing.py`): the categories that mean the agent
// failed or did something it should not have come first, then wrong behavior
// and wrong setup, then the evaluator-review and improvement categories. A
// category this build has no entry for sorts after every known one.
const CATEGORY_PRIORITY: Record<string, number> = {
  failure_pattern: 0,
  policy_safety: 1,
  unexpected_behavior: 2,
  config_error: 3,
  metric_disagreement: 4,
  test_case_improvement: 5,
  metric_improvement: 6,
}

// "Still happening": a finding seen inside this window outranks one that has
// gone quiet, whatever its history.
const RECENTLY_SEEN_WINDOW_MS = 24 * 60 * 60 * 1000

function investigationCategoryRank(category: string): number {
  return CATEGORY_PRIORITY[category] ?? Object.keys(CATEGORY_PRIORITY).length
}

export function investigationInstant(value: string): number {
  const parsed = Date.parse(value)
  // An unparseable timestamp must not poison a comparison; it sorts behind every
  // real instant instead of claiming recency it cannot prove.
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

// Triage order for a list of findings: most severe category first; within a
// category, what is still happening (seen in the last day) before what has gone
// quiet; within that, the most repeated finding, then a returned one, then the
// most recently seen. `now` is injectable so the order is deterministic in tests.
export function compareInvestigationPriority(
  a: InvestigationFinding,
  b: InvestigationFinding,
  now: number = Date.now(),
): number {
  const categoryOrder =
    investigationCategoryRank(a.category) -
    investigationCategoryRank(b.category)
  if (categoryOrder !== 0) {
    return categoryOrder
  }
  const aLastSeen = investigationInstant(a.lastSeen)
  const bLastSeen = investigationInstant(b.lastSeen)
  const aActive = now - aLastSeen <= RECENTLY_SEEN_WINDOW_MS
  const bActive = now - bLastSeen <= RECENTLY_SEEN_WINDOW_MS
  if (aActive !== bActive) {
    return aActive ? -1 : 1
  }
  if (a.occurrenceCount !== b.occurrenceCount) {
    return b.occurrenceCount - a.occurrenceCount
  }
  if (a.isRegression !== b.isRegression) {
    return a.isRegression ? -1 : 1
  }
  if (aLastSeen !== bLastSeen) {
    return bLastSeen - aLastSeen
  }
  return a.findingId.localeCompare(b.findingId)
}

export function investigationCategoryLabel(category: string): string {
  const label = FINDING_CATEGORY_LABELS[category]
  if (label) {
    return label
  }
  const humanized = category.replaceAll("_", " ")
  return humanized.charAt(0).toUpperCase() + humanized.slice(1)
}

function mapFinding(finding: z.infer<typeof findingSchema>) {
  return {
    briefingPriorityReason: finding.briefing_priority_reason,
    category: finding.category,
    categoryLabel: investigationCategoryLabel(finding.category),
    evidenceCount: finding.evidence_count,
    findingId: finding.finding_id,
    firstSeen: finding.first_seen,
    isRegression: Boolean(finding.reopened_from),
    lastSeen: finding.last_seen,
    narrative: finding.narrative,
    occurrenceCount: finding.occurrence_count,
    patternId: finding.pattern_id ?? finding.finding_id,
    previousEpisodeId: finding.previous_episode_id ?? null,
    resolutionNote: finding.resolution_note ?? null,
    resolvedAt: finding.resolved_at ?? null,
    status: finding.status,
    title: finding.title,
    version: finding.version,
  } satisfies InvestigationFinding
}

function mapFreshness(
  entry: z.infer<typeof freshnessEntrySchema>,
): InvestigationFreshnessEntry | null {
  if (entry === null) {
    return null
  }
  return {
    analyzedThrough: entry.analyzed_through,
    updatedAt: entry.updated_at,
  }
}

export function parseInvestigationFindings(
  value: unknown,
): InvestigationFindingsPage | null {
  const parsed = findingsResponseSchema.safeParse(value)
  if (!parsed.success) {
    return null
  }
  return {
    briefing: parsed.data.briefing
      ? {
          eligibleFindingCount: parsed.data.briefing.eligible_finding_count,
          generatedAt: parsed.data.briefing.generated_at,
          selectionMode: parsed.data.briefing.selection_mode ?? "window",
          truncated: parsed.data.briefing.truncated,
          windowStart: parsed.data.briefing.window_start,
        }
      : undefined,
    findings: parsed.data.findings.map(mapFinding),
    freshness: {
      conversation: mapFreshness(parsed.data.freshness.conversation),
      simulation: mapFreshness(parsed.data.freshness.simulation),
    },
    nextPageToken: parsed.data.next_page_token,
    workspaceId: parsed.data.workspace.workspace_id,
  }
}

// Mirrors Sofia's entity route map (app-covi actionRoutes) without importing the
// app-covi module: home components may not depend on it.
export function investigationSourceHref(
  basePath: string,
  sourceType: "conversation" | "simulation",
  sourceId: string,
): string | undefined {
  if (!basePath) {
    return undefined
  }
  if (sourceType === "simulation") {
    return `${basePath}/simulated/results/${encodeURIComponent(sourceId)}`
  }
  return `${basePath}/uploaded/results/${encodeURIComponent(sourceId)}`
}

export function parseInvestigationEvidence(
  value: unknown,
  basePath: string,
): InvestigationEvidencePage | null {
  const parsed = evidenceResponseSchema.safeParse(value)
  if (!parsed.success) {
    return null
  }
  return {
    items: parsed.data.evidence.map((item) => ({
      createdAt: item.created_at,
      evidenceId: item.evidence_id,
      href: investigationSourceHref(basePath, item.source_type, item.source_id),
      metricVerdict: item.metric_verdict ?? null,
      quote: item.quote,
      sourceId: item.source_id,
      sourceLabel:
        item.source_type === "simulation"
          ? "View simulation"
          : "View conversation",
      sourceType: item.source_type,
      speaker: item.speaker,
      turnIndex: item.turn_index,
    })),
    nextPageToken: parsed.data.next_page_token,
  }
}

export function parseInvestigationActionResponse(
  value: unknown,
): InvestigationFinding | null {
  const parsed = actionResponseSchema.safeParse(value)
  return parsed.success ? mapFinding(parsed.data.finding) : null
}

export function parseInvestigationActivity(value: unknown): {
  items: InvestigationFindingActivity[]
  nextPageToken: string | null
} | null {
  const parsed = activityResponseSchema.safeParse(value)
  if (!parsed.success) {
    return null
  }
  return {
    items: parsed.data.activity.map((item) => ({
      activityId: item.activity_id,
      actorType: item.actor_type,
      createdAt: item.created_at,
      eventType: item.event_type,
      findingVersion: item.finding_version,
      note: item.note,
      remediationLabel: item.remediation_label,
      remediationUrl: item.remediation_url,
    })),
    nextPageToken: parsed.data.next_page_token,
  }
}
